import * as angular from 'angular';
import './contact-details.scss';
import { OnInit, Component, Input } from 'ng-metadata/core';
import { MediaContactDocument, MediaContactEmail, MediaContactPhoneNumber, MediaContactSocialProfile } from '../media-contact-document.model';

/**
 * @ngdoc directive
 * @name mwMediaContactDocumentDetails
 * @module mw.components.contentDocuments
 *
 * @param {object} [contact] - The content document. See
 * [Media Conctact Document Model](https://github.com/meltwater/gaf-ng-components/blob/master/src/lib/components/media-contact-document/media-contact-document.model.ts)
 * @description
 * `<mw-media-contact-document-details>` is a component used to display a single media content document.
 *
 * @usage
 * <hljs hljs-language="html">
 *     <mw-media-contact-document-details
 *          class="mw-media-contact-document-details"
 *          contact="$ctrl.contact">
 *     </mw-media-contact-document-details>
 * </hljs>
 */

const template = `
    <div class="mw-media-contact-document-details__left-fill"></div>
    <div class="mw-media-contact-document-details__content">
        <div class="mw-media-contact-document-details__summary"
            ng-if="$ctrl.contact.description || $ctrl.shortSummary">
            <div class="mw-media-contact-document-details__header" translate="mw-media-contact-document__contact-details--summary"></div>
            <div class="mw-media-contact-document-details__text" mw-truncate="150">
                {{$ctrl.contact.description}}
            </div>
        </div>
        <div class="mw-media-contact-document-details__contact" ng-if="$ctrl.primaryPhone || $ctrl.primaryEmail">
            <div class="mw-media-contact-document-details__header" translate="mw-media-contact-document__contact-details--contact"></div>
            <div class="mw-media-contact-document-details__contact-info mw-media-contact-document-details__phone" ng-if="$ctrl.primaryPhone">
                <div>
                    <md-icon md-svg-icon="hardware:ic_smartphone_24px"></md-icon>
                </div>
                <div>
                    <div class="mw-media-contact-document-details__contact-info-sub-header" translate="mw-media-contact-document__contact-details--work-phone"></div>
                    <div class="mw-media-contact-document-details__contact-info-text">
                        {{$ctrl.primaryPhone.number}}
                    </div>
                </div>
            </div>
            <div class="mw-media-contact-document-details__contact-info mw-media-contact-document-details__email" ng-if="$ctrl.primaryEmail">
                <div>
                    <md-icon md-svg-icon="communication:ic_email_24px"></md-icon>
                </div>
                <div>
                    <div class="mw-media-contact-document-details__contact-info-sub-header" translate="mw-media-contact-document__contact-details--work-email"></div>
                    <div class="mw-media-contact-document-details__contact-info-text">
                        <a href="mailto:{{$ctrl.primaryEmail.address}}">
                            {{$ctrl.primaryEmail.address}}
                        </a>
                    </div>
                </div>
            </div>
        </div>
        <div class="mw-media-contact-document-details__social" ng-if="$ctrl.socialProfiles.length > 0">
            <div class="mw-media-contact-document-details__header" translate="mw-media-contact-document__contact-details--social-profiles"></div>
            <md-icon
                ng-repeat="profile in $ctrl.socialProfiles"
                class="mw-media-contact-document-details__social-icon mw-media-contact-document-details__social-{{profile.type}}"
                ng-click="$ctrl.handleGoToProfile(profile.url)"
                md-svg-icon="{{profile.icon}}">
            </md-icon>
        </div>
        <div class="mw-media-contact-document-details__engagement"
            ng-if="$ctrl.contact.engagement">
            <div class="mw-media-contact-document-details__header" translate="mw-media-contact-document__contact-details--engagement"></div>
            <ul class="mw-media-contact-document-details__list">
                <li class=" mw-media-contact-document-details__text">
                    {{$ctrl.contact.engagement.emailsSent}} {{'mw-media-contact-document__contact-details--emails-sent' | translate}}
                </li>
                <li class=" mw-media-contact-document-details__text">
                    {{$ctrl.contact.engagement.openRate}}% {{'mw-media-contact-document__contact-details--open-rate' | translate}}
                </li>
                <li class=" mw-media-contact-document-details__text">
                    {{$ctrl.contact.engagement.clickRate}}% {{'mw-media-contact-document__contact-details--click-rate' | translate}}
                </li>
            </ul>
        </div>
        <div class="mw-media-contact-document-details__lists"
            ng-if="$ctrl.contact.lists">
            <div class="mw-media-contact-document-details__header" translate="mw-media-contact-document__contact-details--lists"></div>
            <ul class="mw-media-contact-document-details__list">
                <li class=" mw-media-contact-document-details__text" ng-repeat="item in $ctrl.contact.lists">
                    {{item}}
                </li>
            </ul>
        </div>
    </div>
`;

@Component({
    selector: 'mw-media-contact-document-details',
    template: template
})
export class MediaContactDocumentDetailsComponent implements OnInit {
    @Input('<') contact: MediaContactDocument;

    private primaryEmail: MediaContactEmail | undefined;
    private primaryPhone: MediaContactPhoneNumber | undefined;
    private shortSummary: string = '';
    private showMore: boolean = false;
    private socialProfiles: MediaContactSocialProfile[];
    private iconMap: { [type: string]: string } = {
        facebook: 'meltwater:facebook',
        linked_in: 'meltwater:linkedin',
        twitter: 'meltwater:twitter',
        googleplus: 'meltwater:gplus',
        instagram: 'meltwater:instagram',
        pinterest: 'meltwater:pinterest',
        youtube: 'meltwater:youtube',
        xing: 'meltwater:xing',
    };

    ngOnInit() {
        this.primaryPhone = this.contact.phoneNumbers.filter(p => p.primary === true)[0];
        this.primaryEmail = this.contact.emails.filter(e => e.primary === true)[0];

        this.socialProfiles = this.contact.socialProfiles.map(s => ({
            ...s,
            icon: this.iconMap[s.type],
        }));
    }

    handleToggleShowMore() {
        this.showMore = !this.showMore
    }

    handleGoToProfile(url: string) {
        window.open(url, '_blank')
    }
}


// WEBPACK FOOTER //
// ./src/lib/components/content-documents/media-contact-document/contact-details/contact-details.component.ts